<template>
  <div class="medical-record-item">
    <div class="card">
      <div class="card-header">
        <strong>
          {{ item.startDate | date('DD[ de ]MMMM[ de ]YYYY[ às ]HH:mm') }}
        </strong><br>
        <small>{{ item.professional.name }}</small>
      </div>
      <div class="card-body">
        <ophthalmology-item
          v-if="item.anamnesis.templateRenderer === 'ophthalmology'"
          :item="item"
        />
        <odontogram-item
          v-else-if="item.anamnesis.templateRenderer === 'odontogram'"
          :item="item"
        />
        <default-item
          v-else
          :item="item"
        />

        <template v-if="hasRelatedRecords">
          <div class="divider" />

          <div class="text-bold mb-2">Documentos emitidos</div>
          <div class="mb-2" v-if="item.prescriptions > 0">
            <strong class="hover-link" :class="{loading: loadingItem === 'prescription'}"
                    @click="openPrescription">
              ({{ item.prescriptions }})
              Prescriç<span v-if="item.prescriptions === 1">ão</span><span v-else>ões</span>
            </strong>
            <prescription-detail
              :id="item.id"
              :items="detailData.prescriptions"
              v-if="show.prescription"
            />
          </div>
          <div class="mb-2" v-if="item.documents > 0">
            <strong class="hover-link" :class="{loading: loadingItem === 'document'}"
                    @click="openDocument">
              ({{ item.documents }}) Impresso<span v-if="item.documents > 1">s</span>
            </strong>
            <document-detail
              :id="item.id"
              :items="detailData.documents"
              v-if="show.document"
            />
          </div>
          <div class="mb-2" v-if="item.recipes > 0">
            <strong class="hover-link" :class="{loading: loadingItem === 'recipe'}"
                    @click="openRecipe">
              ({{ item.recipes }}) Receita<span v-if="item.recipes > 1">s</span>
            </strong>
            <recipe-detail
              :id="item.id"
              :items="detailData.recipes"
              v-if="show.recipe"
            />
          </div>
          <div class="mb-2" v-if="item.examRequests > 0">
            <strong class="hover-link" :class="{loading: loadingItem === 'request'}"
                    @click="openRequest">({{ item.examRequests }})
              <span v-if="item.examRequests === 1">Solicitação de exames</span>
              <span v-else>Solicitações de exames</span>
            </strong>
            <request-detail
              :id="item.id"
              :items="detailData.examRequests"
              v-if="show.request"
            />
          </div>
          <div class="mb-2" v-if="item.exams > 0">
            <strong class="hover-link" :class="{loading: loadingItem === 'exam'}"
                    @click="openExam">({{ item.exams }})
              <span v-if="item.exams === 1">Exame</span>
              <span v-else>Exames</span>
            </strong>
            <exams-view
              v-if="show.exam"
              :patient-id="item.patient.id"
              :id="item.id"
              :show-edit-button="false"
              :items="detailData.exams"
            />
          </div>
          <div class="mb-2" v-if="item.hospitalizationRequests > 0">
            <strong class="hover-link" :class="{loading: loadingItem === 'hospitalization'}"
                    @click="openHospitalization">
              ({{ item.hospitalizationRequests }})
              <span v-if="item.hospitalizationRequests === 1">Solicitação de internação</span>
              <span v-else>Solicitações de internação</span>
            </strong>
            <hospitalization-detail
              :id="item.id"
              :items="detailData.hospitalizationRequests"
              v-if="show.hospitalization"
            />
          </div>
          <div class="mb-2" v-if="item.attachments > 0">
            <strong class="hover-link" :class="{loading: loadingItem === 'attachment'}"
                    @click="openAttachment">
              ({{ item.attachments }}) Anexo<span v-if="item.attachments > 1">s</span>
            </strong>
            <attachment-detail
              :id="item.id"
              :patientId="item.patient.id"
              v-if="show.attachment"
            />
          </div>
        </template>
      </div>
      <div class="card-footer">
        <slot name="footer">
          <div></div>
          <div class="text-right">
            <template v-if="canView">
              <button
                type="button"
                class="btn btn-sm ml-1 btn-icon btn-icon-left"
                :class="{loading: printing}"
                :disabled="printing"
                @click="printAnamnesis"
              ><fa-icon :icon="['fal', 'print']" /> Imprimir</button>
              <button
                type="button"
                class="btn btn-sm ml-1 btn-icon btn-icon-left"
                @click="openDetail"
              ><fa-icon :icon="['fal', 'poll-h']" /> Detalhar</button>
            </template>
            <div class="label text-info p-1 pl-2 pr-2" v-else>
              <fa-icon :icon="['fal', 'user-lock']" /> Atendimento sigiloso
            </div>
          </div>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import DefaultItem from './item/DefaultItem.vue';
import OphthalmologyItem from './item/OphthalmologyItem.vue';
import OdontogramItem from './item/OdontogramItem.vue';
import HospitalizationDetail from './request/HospitalizationDetail.vue';
import RecipeDetail from './receipt/Detail.vue';
import DocumentDetail from './document/Detail.vue';
import AttachmentDetail from './attachment/Detail.vue';
import RequestDetail from './request/Detail.vue';
import PrescriptionDetail from './prescription/Detail.vue';
import ExamsView from './exam/View.vue';

export default {
  props: {
    item: {
      type: Object,
    },
  },
  components: {
    PrescriptionDetail,
    RequestDetail,
    AttachmentDetail,
    DocumentDetail,
    RecipeDetail,
    HospitalizationDetail,
    DefaultItem,
    OphthalmologyItem,
    OdontogramItem,
    ExamsView,
  },
  data() {
    return {
      loadingItem: null,
      printing: false,
      show: {
        document: false,
        recipe: false,
        request: false,
        exam: false,
        hospitalization: false,
        attachment: false,
        prescription: false,
      },
      detailData: {
        id: '',
        attachments: false,
        patientId: '',
        anamnesis: {
          templateId: '',
          templateRenderer: null,
          inputs: [],
        },
        anthropometry: {
          abdominalCircumference: '',
          cephalicPerimeter: '',
          height: '',
          weight: '',
        },
        vitalSigns: {
          bloodPressure: '',
          heartRate: '',
          respiratoryFrequency: '',
          saturation: '',
          temperature: '',
        },
        examRequests: [],
        hospitalizationRequests: [],
        recipes: [],
        documents: [],
        prescriptions: [],
        exams: [],
      },
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    hasRelatedRecords() {
      const recordTypes = [
        'documents',
        'recipes',
        'prescriptions',
        'examRequests',
        'exams',
        'hospitalizationRequests',
        'attachments',
      ];

      return recordTypes.some(type => this.item[type] > 0);
    },
    canView() {
      return this.item.private
        ? this.item.professional && this.item.professional.id === this.user.id
        : true;
    },
  },
  methods: {
    printAnamnesis() {
      this.printing = true;
      return this.$file
        .print(`/medical-records/${this.item.id}/print`)
        .catch(this.$toast.error)
        .then(() => {
          this.printing = false;
        });
    },
    openDetail() {
      this.$emit('openDetail', this.item.id);
    },
    async openPrescription() {
      this.loadingItem = 'prescription';
      this.show.prescription = !this.show.prescription;
      if (!this.detailData.id) {
        await this.loadDetail();
      }
      this.loadingItem = null;
    },
    async openDocument() {
      this.loadingItem = 'document';
      this.show.document = !this.show.document;
      if (!this.detailData.id) {
        await this.loadDetail();
      }
      this.loadingItem = null;
    },
    async openRecipe() {
      this.loadingItem = 'recipe';
      this.show.recipe = !this.show.recipe;
      if (!this.detailData.id) {
        await this.loadDetail();
      }
      this.loadingItem = null;
    },
    async openRequest() {
      this.loadingItem = 'request';
      this.show.request = !this.show.request;
      if (!this.detailData.id) {
        await this.loadDetail();
      }
      this.loadingItem = null;
    },
    async openExam() {
      this.loadingItem = 'exam';
      this.show.exam = !this.show.exam;
      if (!this.detailData.id) {
        await this.loadDetail();
      }
      this.loadingItem = null;
    },
    async openHospitalization() {
      this.loadingItem = 'hospitalization';
      this.show.hospitalization = !this.show.hospitalization;
      if (!this.detailData.id) {
        await this.loadDetail();
      }
      this.loadingItem = null;
    },
    async openAttachment() {
      this.loadingItem = 'attachment';
      this.show.attachment = !this.show.attachment;
      if (!this.detailData.id) {
        await this.loadDetail();
      }
      this.loadingItem = null;
    },
    loadDetail() {
      return this.$httpX.get(`/medical-records/${this.item.id}`)
        .then(({ data }) => {
          this.detailData = data;
        });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

.medical-record-item {
  border: $border-width solid $border-color;
  border-radius: $border-radius;
  margin-bottom: $layout-spacing-lg;
  .negative-value {
    color: red;
  }
  .positive-value {
    color: green;
  }
  .card-footer {
    display: flex;
    justify-content: space-between;
    .image {
      color: $primary-color;
      font-size: .8rem;
    }
  }
  .card-detail {
    background-color: lighten($gray-color-light, 5%);
    border: $border-color solid $border-width;
    border-radius: $border-radius;
    margin: $layout-spacing 0;
    padding: $layout-spacing;
    .card-detail-title {
      font-weight: bold;
      margin-bottom: $layout-spacing;
    }
    .card-drug {
      background-color: $gray-color-light;
      border: $border-color solid $border-width;
      border-radius: $border-radius;
      margin-bottom: $layout-spacing;
      padding: $layout-spacing;
    }
    table {
      border-collapse: separate;
      border-spacing: 0;
      min-width: 50%;
      tr {
        th, td {
          border-bottom: $border-width solid $border-color;
          border-left: $border-width solid $border-color;
          padding: $layout-spacing-sm + $border-width-lg $layout-spacing;
          text-align: left;
        }
        td:first-child, th:first-child {
          border-left: $border-width solid $dark-blue-color-300;
        }
        td:last-child, th:last-child {
          border-right: $border-width solid $dark-blue-color-300;
        }
        &:first-child {
          th {
            border-top: $border-width solid $dark-blue-color-300;
          }
          th:first-child {
            border-top-left-radius: $border-radius;
          }
          th:last-child {
            border-top-right-radius: $border-radius;
          }
        }
        &:last-child {
          td {
            border-bottom: $border-width solid $dark-blue-color-300;
          }
          td:first-child {
            border-bottom-left-radius: $border-radius;
          }
          td:last-child {
            border-bottom-right-radius: $border-radius;
          }
        }
      }
    }
  }
}
</style>
